// frontend/packages/business-console/src/components/build-interview/BuildProgressBanner.jsx
//
// Home re-entry card for the build flow. A customer with an active build is
// routed straight into the build step at login; this banner makes sure they
// are still guided back in if they land on Home (refresh, returning visit).
// It fetches the lightweight build status and, only when there is an active
// build, renders a single clear "continue" call to action that deep-links to
// the right step (interview / review / go-live). Renders nothing otherwise,
// so it is invisible to operators with no build in flight.

import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { getBuildStatus } from '../../services/buildFunnelService';

const BRAND_GRADIENT =
  'linear-gradient(120deg, #2563eb 0%, #7c3aed 55%, #ab2ddd 100%)';

const STAGE_COPY = {
  interview: {
    title: 'Build your website',
    body: 'Answer a few quick questions and our AI drafts your site for you. It takes about 5 minutes.',
    cta: 'Start building',
  },
  review: {
    title: 'Your website draft is ready',
    body: 'Take a look at what we built. Approve it, or tell us what to change.',
    cta: 'Review my site',
  },
  go_live: {
    title: 'You are almost live',
    body: 'One last step: connect your domain to publish your site.',
    cta: 'Finish going live',
  },
};

export default function BuildProgressBanner() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    getBuildStatus()
      .then((s) => {
        if (alive) setStatus(s);
      })
      .catch(() => {
        if (alive) setStatus(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (loading) return null;
  if (!status || !status.hasActiveBuild || !status.routeSuffix) return null;

  const copy = STAGE_COPY[status.stage];
  if (!copy) return null;

  const go = () => navigate(`/business/${slug}/${status.routeSuffix}`);

  return (
    <Box
      onClick={go}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') go();
      }}
      sx={{
        mb: 3,
        p: { xs: 2.5, sm: 3 },
        borderRadius: 3,
        cursor: 'pointer',
        color: '#fff',
        backgroundImage: BRAND_GRADIENT,
        boxShadow: '0 12px 30px rgba(124, 92, 246, 0.30)',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 220 }}>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            opacity: 0.85,
          }}
        >
          Your website
        </Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 800, mt: 0.5 }}>
          {copy.title}
        </Typography>
        <Typography
          sx={{ fontSize: 14.5, mt: 0.5, opacity: 0.92, lineHeight: 1.5 }}
        >
          {copy.body}
        </Typography>
      </Box>
      <Button
        onClick={(e) => {
          e.stopPropagation();
          go();
        }}
        variant="contained"
        sx={{
          bgcolor: '#fff',
          color: '#4c1d95',
          fontWeight: 800,
          px: 3,
          py: 1.2,
          '&:hover': { bgcolor: '#f3f0ff' },
        }}
      >
        {copy.cta}
      </Button>
    </Box>
  );
}
